import { localDayOf } from "../../shared/format/local-date.js";
import { createIcons, Plus, FileText, Download, Sparkles, Trash2, Eye, CheckCircle, AlertCircle, AlertTriangle, Search, Filter, Edit, CheckSquare, Clock, Calendar, Building, MapPin, User, RotateCcw, X } from "lucide";
import { contextStore } from "../auth/context-store.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { router } from "../router/router.js";
import type { InspectionId, IntegrityReportId } from "../../shared/kernel/identifiers.js";
import "../components/document/pdf-preview.js";
import { documentEngine } from "../../infrastructure/documents/document-engine.js";
import { AppError } from "../../shared/errors/app-error.js";
import { CONTEXT_LOAD_STYLES, ContextLoadTracker } from "../context/context-load.js";

class AppInspectionsPage extends HTMLElement {
  /**
   * Tratamento único das leituras de contexto (política em
   * presentation/context/context-load.ts). Um erro de permissão deixa de
   * virar lista vazia e de produzir "nenhum cadastro" com o cadastro
   * existente na base.
   */
  private readonly ctxLoad = new ContextLoadTracker();

  private inspections: any[] = [];
  private reports: any[] = [];
  private equipments: any[] = [];
  private clients: any[] = [];
  private professionals: any[] = [];
  private users: any[] = [];
  private unitAreas: any[] = [];
  
  // View state & Filters
  private searchTerm: string = "";
  private filterClient: string = "";
  private filterUnit: string = "";
  private filterInspector: string = "";
  private filterDateStart: string = "";
  private filterDateEnd: string = "";
  private activeMainTab: 'ALL' | 'IN_PROGRESS' | 'COMPLETED' = 'ALL';
  private activeSubTab: string = 'ALL';
  
  private inspectionToDelete: any = null;

  connectedCallback() {
    this.filterClient = contextStore.get().activeClientId || "";
    this.loadData();
  }

  private async loadData() {
    this.innerHTML = `
      <style>${CONTEXT_LOAD_STYLES}</style>
      ${this.ctxLoad.banner()}
      <div style="padding: 56px 24px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; color: var(--color-text-muted);">
        <svg class="spinner spinner-dark spinner-lg" viewBox="0 0 24 24" fill="none" aria-label="Carregando...">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-opacity="0.2" stroke-width="3"/>
          <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
        </svg>
        <span class="text-body-small">Carregando inspeções e relatórios...</span>
      </div>
    `;
    
    try {
      const p = window.app.platform;
      const ctx = {
        activeClientId: this.filterClient || contextStore.get().activeClientId,
        userId: contextStore.get().userId,
        permissions: contextStore.get().permissions
      } as any;

      const [inspections, reports, equipments, clients, professionals, users, unitAreas] = await Promise.all([
        p.inspectionService.listAllInspections(ctx).catch(() => []),
        p.reportService.listReports(ctx).catch(() => []),
        await this.ctxLoad.load("Equipamentos", () => p.equipmentService.listEquipments(ctx)),
        await this.ctxLoad.load("Clientes", () => p.adminService.listClients(ctx)),
        await this.ctxLoad.load("Profissionais", () => p.adminService.listProfessionals(ctx)),
        p.adminService.listUsers(ctx).catch(() => []),
        p.projectService ? await this.ctxLoad.load("Unidades / Áreas", () => p.projectService.listUnitAreas(undefined, undefined, ctx)) : []
      ]);

      this.inspections = Array.from(inspections || []);
      this.reports = Array.from(reports || []);
      this.equipments = Array.from(equipments || []);
      this.clients = Array.from(clients || []);
      this.professionals = Array.from(professionals || []);
      this.users = Array.from(users || []);
      this.unitAreas = Array.from(unitAreas || []);

      this.render();
    } catch (e: any) {
      this.innerHTML = `<div style="padding: 40px; color: var(--status-danger-solid);">Erro ao carregar inspeções: ${e.message}</div>`;
    }
  }

  private getReportForInspection(inspId: string) {
    return this.reports.find(r => r.inspectionId === inspId);
  }

  private getClientDisplayName(clientId?: string | null): string {
    const targetId = clientId || contextStore.get().activeClientId;
    if (!targetId) return contextStore.get().activeClientName || 'Cliente';
    const cli = this.clients.find(c => c.id === targetId || c.code === targetId);
    return cli?.fantasyName || cli?.name || cli?.corporateName || contextStore.get().activeClientName || targetId;
  }

  private getInspectorDisplayName(insp: any, rep: any): string {
    // 1. Direct inspectorName property if present on report or inspection
    if (rep?.inspectorName) return rep.inspectorName;
    if (insp?.inspectorName) return insp.inspectorName;

    const rawId = rep?.inspectorId || insp?.inspectorId;
    if (rawId) {
      // Look up in professionals
      const prof = this.professionals.find(p => p.id === rawId || p.name === rawId);
      if (prof && prof.name) return prof.name;

      // Look up in users
      const user = this.users.find(u => u.id === rawId || u.username === rawId || u.name === rawId);
      if (user && user.name) return user.name;

      // If rawId is already a human name (not an ID code like PROF-1)
      if (!rawId.startsWith('PROF-') && !rawId.startsWith('usr_') && !rawId.startsWith('user-')) {
        return rawId;
      }
    }

    // 2. The author/inspector who is currently logged in and elaborated the report
    if (contextStore.get().userName) {
      return contextStore.get().userName;
    }

    // 3. First registered inspector
    const defaultProf = this.professionals.find(p => p.functionTitle?.toLowerCase().includes('inspetor'));
    if (defaultProf) return defaultProf.name;

    return 'Inspetor Responsável';
  }

  private getAvailableClients(): { id: string; name: string }[] {
    const clientMap = new Map<string, string>();
    this.clients.forEach(c => {
      const name = c.fantasyName || c.name || c.corporateName || c.id;
      clientMap.set(c.id, name);
    });
    this.inspections.forEach(insp => {
      if (insp.clientId && !clientMap.has(insp.clientId)) {
        clientMap.set(insp.clientId, this.getClientDisplayName(insp.clientId));
      }
    });
    return Array.from(clientMap.entries()).map(([id, name]) => ({ id, name }));
  }

  private getAvailableUnits(): { id: string; name: string }[] {
    const unitMap = new Map<string, string>();
    
    // Units from project unitAreas
    this.unitAreas.forEach(u => {
      if (u.id) {
        const displayName = u.code && u.name ? `${u.code} - ${u.name}` : (u.name || u.code || u.id);
        unitMap.set(u.id, displayName);
      }
    });

    // Units from equipments
    this.equipments.forEach(eq => {
      const uId = eq.unitAreaId || eq.areaId;
      const uName = eq.unitAreaName || eq.plantArea || eq.location || eq.area;
      if (uId && uName && !unitMap.has(uId)) {
        unitMap.set(uId, uName);
      } else if (uName && !unitMap.has(uName)) {
        unitMap.set(uName, uName);
      }
    });

    // Units from inspections
    this.inspections.forEach(insp => {
      if (insp.unitAreaId && !unitMap.has(insp.unitAreaId)) {
        unitMap.set(insp.unitAreaId, insp.unitAreaId);
      }
    });

    return Array.from(unitMap.entries()).map(([id, name]) => ({ id, name }));
  }

  private getAvailableInspectors(): { id: string; name: string }[] {
    const inspectorMap = new Map<string, string>();

    this.professionals.forEach(p => {
      if (p.name) inspectorMap.set(p.name, p.name);
    });

    this.users.forEach(u => {
      if (u.name) inspectorMap.set(u.name, u.name);
    });

    this.inspections.forEach(insp => {
      const rep = this.getReportForInspection(insp.id);
      const name = this.getInspectorDisplayName(insp, rep);
      if (name && name !== 'Inspetor Responsável' && !inspectorMap.has(name)) {
        inspectorMap.set(name, name);
      }
    });

    if (contextStore.get().userName && !inspectorMap.has(contextStore.get().userName)) {
      inspectorMap.set(contextStore.get().userName, contextStore.get().userName);
    }

    return Array.from(inspectorMap.entries()).map(([id, name]) => ({ id, name }));
  }

  private getFilteredItems() {
    return this.inspections.map(insp => {
      const rep = this.getReportForInspection(insp.id);
      return { insp, rep };
    }).filter(({ insp, rep }) => {
      const eq = this.equipments.find(e => e.id === insp.equipmentId);
      const eqTag = eq?.tag || eq?.name || insp.equipmentId || '';
      const repId = rep?.id || '';
      const clientName = this.getClientDisplayName(insp.clientId || rep?.clientId);
      const inspectorName = this.getInspectorDisplayName(insp, rep);
      const targetClientId = insp.clientId || rep?.clientId || '';
      
      const eqUnitId = eq?.unitAreaId || eq?.areaId || insp?.unitAreaId || '';
      const eqUnitName = eq?.unitAreaName || eq?.plantArea || eq?.location || eq?.area || '';

      // 1. Text Search Filter
      const matchesSearch = !this.searchTerm || 
        insp.id.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        repId.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        eqTag.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        clientName.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        inspectorName.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        eqUnitName.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        (insp.notes && insp.notes.toLowerCase().includes(this.searchTerm.toLowerCase())) ||
        (rep?.reportNumber && rep.reportNumber.toLowerCase().includes(this.searchTerm.toLowerCase()));

      if (!matchesSearch) return false;

      // 2. Client Filter
      if (this.filterClient) {
        const matchClient = targetClientId === this.filterClient || 
                            clientName.toLowerCase() === this.filterClient.toLowerCase() ||
                            targetClientId.toLowerCase() === this.filterClient.toLowerCase();
        if (!matchClient) return false;
      }

      // 3. Unit / Area Filter
      if (this.filterUnit) {
        const matchUnit = eqUnitId === this.filterUnit ||
                          eqUnitName.toLowerCase() === this.filterUnit.toLowerCase() ||
                          (insp.unitAreaId && insp.unitAreaId === this.filterUnit);
        if (!matchUnit) return false;
      }

      // 4. Inspector Filter
      if (this.filterInspector) {
        const rawInspId = rep?.inspectorId || insp.inspectorId || '';
        const matchInspector = rawInspId === this.filterInspector ||
                              inspectorName.toLowerCase() === this.filterInspector.toLowerCase() ||
                              inspectorName.toLowerCase().includes(this.filterInspector.toLowerCase());
        if (!matchInspector) return false;
      }

      // 5. Date Filter (De / Até)
      const rawDate = rep?.updatedAt || rep?.createdAt || insp.createdAt || insp.date;
      if (rawDate) {
        const itemDateStr = localDayOf(rawDate);
        if (this.filterDateStart && itemDateStr < this.filterDateStart) return false;
        if (this.filterDateEnd && itemDateStr > this.filterDateEnd) return false;
      }

      const repStatus = rep ? rep.status : 'DRAFT';

      // 6. Main Tab Filter
      if (this.activeMainTab === 'IN_PROGRESS') {
        const inProgressStatuses = ['IN_ELABORATION', 'FINALIZED_BY_INSPECTOR', 'SUBMITTED_TO_ENGINEERING', 'UNDER_ENGINEERING_REVIEW', 'RETURNED', 'IN_CORRECTION', 'AWAITING_SIGNATURE'];
        if (!inProgressStatuses.includes(repStatus) && repStatus !== 'DRAFT') return false;
      } else if (this.activeMainTab === 'COMPLETED') {
        const completedStatuses = ['APPROVED', 'SIGNED', 'COMPLETED', 'CONCLUDED'];
        if (!completedStatuses.includes(repStatus)) return false;
      }

      // 7. Sub Tab Filter (only applies if IN_PROGRESS)
      if (this.activeMainTab === 'IN_PROGRESS' && this.activeSubTab !== 'ALL') {
        if (this.activeSubTab === 'IN_ELABORATION' && repStatus !== 'IN_ELABORATION' && repStatus !== 'DRAFT') return false;
        if (this.activeSubTab === 'IN_ANALYSIS' && !['SUBMITTED_TO_ENGINEERING', 'UNDER_ENGINEERING_REVIEW'].includes(repStatus)) return false;
        if (this.activeSubTab === 'RETURNED' && !['RETURNED', 'IN_CORRECTION'].includes(repStatus)) return false;
        if (this.activeSubTab === 'AWAITING_SIGNATURE' && repStatus !== 'AWAITING_SIGNATURE') return false;
        if (this.activeSubTab === 'FINALIZED_BY_INSPECTOR' && repStatus !== 'FINALIZED_BY_INSPECTOR') return false;
      }

      return true;
    });
  }

  private render() {
    const hasCreatePermission = contextStore.hasPermission(PermissionCode.InspectionCreate);
    const filteredItems = this.getFilteredItems();
    const availableClients = this.getAvailableClients();
    const availableUnits = this.getAvailableUnits();
    const availableInspectors = this.getAvailableInspectors();
    const hasActiveFilters = Boolean(
      this.searchTerm || 
      this.filterClient || 
      this.filterUnit || 
      this.filterInspector || 
      this.filterDateStart || 
      this.filterDateEnd
    );

    // Calculate KPI cards
    const total = this.inspections.length;
    let emElaboracao = 0;
    let emAnalise = 0;
    let pendencias = 0; // returned
    let concluidos = 0;

    this.inspections.forEach(insp => {
      const rep = this.getReportForInspection(insp.id);
      const st = rep ? rep.status : 'DRAFT';
      if (st === 'IN_ELABORATION' || st === 'DRAFT') emElaboracao++;
      if (['SUBMITTED_TO_ENGINEERING', 'UNDER_ENGINEERING_REVIEW'].includes(st)) emAnalise++;
      if (['RETURNED', 'IN_CORRECTION'].includes(st)) pendencias++;
      if (['APPROVED', 'SIGNED', 'COMPLETED'].includes(st)) concluidos++;
    });

    this.innerHTML = `
      <style>
        .page-container {
          padding: 24px;
          max-width: 1300px;
          margin: 0 auto;
        }
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 16px;
        }
        .page-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--color-text-primary);
          margin: 0;
        }
        
        .kpi-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }
        .kpi-card {
          background: var(--color-graphite-800);
          border: 1px solid var(--color-primary);
          border-radius: 8px;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .kpi-title {
          color: var(--color-graphite-400);
          font-size: 0.875rem;
          font-weight: 500;
        }
        .kpi-value {
          color: var(--color-text-primary);
          font-size: 1.5rem;
          font-weight: 700;
        }

        .filter-card {
          background: var(--color-graphite-800);
          border: 1px solid var(--color-primary);
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 24px;
          display: flex;
          flex-direction: column;
          gap: 18px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }
        .filter-main-row {
          display: flex;
          gap: 12px;
          align-items: center;
          flex-wrap: wrap;
        }
        .search-box {
          flex: 1;
          min-width: 280px;
          position: relative;
        }
        .search-input {
          width: 100%;
          background: var(--color-primary);
          border: 1px solid var(--color-graphite-700);
          color: var(--ia-fg, #F7F8F8);
          padding: 11px 14px 11px 42px;
          border-radius: 8px;
          font-size: 0.875rem;
          box-sizing: border-box;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .search-input:focus {
          outline: none;
          border-color: var(--color-ink);
          background: var(--color-primary);
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
        }
        .btn-clear-filters {
          background: rgba(239, 68, 68, 0.08);
          border: 1px solid rgba(239, 68, 68, 0.25);
          color: var(--status-danger-border);
          padding: 10px 16px;
          border-radius: 8px;
          font-size: 0.8125rem;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s;
          white-space: nowrap;
        }
        .btn-clear-filters:hover {
          background: rgba(239, 68, 68, 0.15);
          color: var(--color-text-primary);
          border-color: var(--status-danger-solid);
        }
        .filter-controls-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          align-items: flex-end;
          width: 100%;
        }
        .filter-group {
          flex: 1 1 210px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .filter-label {
          font-size: 0.75rem;
          font-weight: 700;
          color: var(--color-graphite-400);
          display: flex;
          align-items: center;
          gap: 6px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .filter-select {
          background: var(--color-primary);
          border: 1px solid var(--color-graphite-700);
          color: var(--ia-fg, #F7F8F8);
          padding: 10px 12px;
          border-radius: 8px;
          font-size: 0.875rem;
          cursor: pointer;
          width: 100%;
          box-sizing: border-box;
          transition: all 0.2s ease;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          background-size: 14px;
          padding-right: 36px;
        }
        .filter-select:focus {
          outline: none;
          border-color: var(--color-ink);
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
        }
        .filter-select option {
          background: var(--color-graphite-800);
          color: var(--color-bg-primary);
        }
        .filter-group-date {
          flex: 1.5 1 340px;
        }
        .date-inputs-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
        }
        .filter-date-input {
          background: var(--color-primary);
          border: 1px solid var(--color-graphite-700);
          color: var(--ia-fg, #F7F8F8);
          padding: 9px 12px;
          border-radius: 8px;
          font-size: 0.875rem;
          flex: 1;
          min-width: 120px;
          box-sizing: border-box;
          color-scheme: dark;
          transition: all 0.2s ease;
        }
        .filter-date-input:focus {
          outline: none;
          border-color: var(--color-ink);
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
        }


        .tabs-container {
          display: flex;
          gap: 8px;
          margin-bottom: 16px;
          border-bottom: 1px solid var(--color-primary);
          padding-bottom: 2px;
        }
        .tab {
          padding: 8px 16px;
          color: var(--color-graphite-400);
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          border-bottom: 2px solid transparent;
        }
        .tab.active {
          color: var(--color-ink);
          border-bottom-color: var(--color-ink);
        }

        .sub-tabs-container {
          display: flex;
          gap: 8px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }
        .sub-tab {
          padding: 6px 12px;
          background: var(--color-primary);
          color: var(--color-graphite-300);
          border: 1px solid var(--color-graphite-700);
          border-radius: 16px;
          font-size: 0.8125rem;
          font-weight: 500;
          cursor: pointer;
        }
        .sub-tab.active {
          background: var(--color-primary);
          color: var(--color-text-primary);
          border-color: var(--color-primary);
        }

        .table-container {
          background: var(--color-graphite-800);
          border: 1px solid var(--color-primary);
          border-radius: 8px;
          overflow-x: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          text-align: left;
          color: var(--color-graphite-300);
        }
        th {
          background: var(--color-primary);
          padding: 12px 16px;
          font-size: 0.8125rem;
          font-weight: 600;
          color: var(--color-graphite-300);
          border-bottom: 1px solid var(--color-graphite-700);
        }
        td {
          padding: 12px 16px;
          font-size: 0.875rem;
          border-bottom: 1px solid var(--color-primary);
          vertical-align: middle;
        }
        tr:hover td {
          background: var(--color-primary);
        }

        .badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
        }
        .badge-success { background: rgba(34, 197, 94, 0.15); color: var(--status-success-text); border: 1px solid rgba(34, 197, 94, 0.3); }
        .badge-warning { background: var(--status-warning-bg); color: var(--status-warning-text); border: 1px solid var(--status-warning-border); }
        .badge-info { background: rgba(59, 130, 246, 0.15); color: var(--ia-info-fg, #60a5fa); border: 1px solid rgba(59, 130, 246, 0.3); }
        .badge-neutral { background: rgba(148, 163, 184, 0.15); color: var(--color-graphite-400); border: 1px solid rgba(148, 163, 184, 0.25); }
        .badge-danger { background: rgba(239, 68, 68, 0.15); color: var(--status-danger-solid); border: 1px solid rgba(239, 68, 68, 0.3); }

        .btn-action {
          background: transparent;
          border: none;
          color: var(--color-graphite-400);
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.15s;
        }
        .btn-action:hover {
          background: var(--color-graphite-700);
          color: var(--color-text-primary);
        }
        .btn-action.primary { color: var(--color-ink); }
        .btn-action.success { color: var(--status-success-text); }
        .btn-action.danger { color: var(--status-danger-solid); }

        .btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background-color: var(--ia-action-bg, var(--color-primary));
          color: var(--ia-action-fg, var(--color-text-primary));
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          font-size: 0.875rem;
        }
        
        .doc-modal {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.85); z-index: 1000;
          display: none; padding: 24px; box-sizing: border-box;
        }
        .doc-modal.active { display: flex; flex-direction: column; }
        .doc-modal-content {
          flex: 1; background: var(--color-graphite-800); border-radius: 8px;
          overflow: hidden; display: flex; flex-direction: column;
          border: 1px solid var(--color-graphite-700);
        }
      </style>

      <div class="page-container">
        <div class="page-header">
          <div>
            <h1 class="page-title">Gestão de Inspeções</h1>
            <p style="color: var(--color-graphite-400); font-size: 0.875rem; margin-top: 4px;">Central de relatórios e inspeções (RI)</p>
          </div>
          ${hasCreatePermission ? `
            <button class="btn-primary" id="btn-new-inspection">
              <i data-lucide="plus" style="width: 16px; height: 16px;"></i> Nova Inspeção
            </button>
          ` : ''}
        </div>

        <div class="kpi-row">
          <div class="kpi-card">
            <span class="kpi-title">Total</span>
            <span class="kpi-value">${total}</span>
          </div>
          <div class="kpi-card">
            <span class="kpi-title">Em elaboração</span>
            <span class="kpi-value" style="color: var(--ia-info-fg, #60a5fa);">${emElaboracao}</span>
          </div>
          <div class="kpi-card">
            <span class="kpi-title">Em análise</span>
            <span class="kpi-value" style="color: var(--status-warning-text);">${emAnalise}</span>
          </div>
          <div class="kpi-card">
            <span class="kpi-title">Com pendências</span>
            <span class="kpi-value" style="color: var(--status-danger-solid);">${pendencias}</span>
          </div>
          <div class="kpi-card">
            <span class="kpi-title">Concluídos</span>
            <span class="kpi-value" style="color: var(--status-success-text);">${concluidos}</span>
          </div>
        </div>

        <!-- Filter Bar with Client, Unit, Inspector and Date options -->
        <div class="filter-card">
          <div class="filter-main-row">
            <div class="search-box">
              <i data-lucide="search" style="position:absolute; left:12px; top:11px; width:16px; height:16px; color: var(--color-text-secondary);"></i>
              <input type="text" class="search-input" id="search-input" placeholder="Buscar por TAG, RI, equipamento ou sistema..." value="${this.searchTerm}">
            </div>
            ${hasActiveFilters ? `
              <button type="button" class="btn-clear-filters" id="btn-clear-filters" title="Limpar todos os filtros">
                <i data-lucide="rotate-ccw" style="width: 14px; height: 14px;"></i>
                <span>Limpar Filtros</span>
              </button>
            ` : ''}
          </div>

          <div class="filter-controls-grid">
            <!-- Filtro Cliente -->
            <div class="filter-group">
              <label class="filter-label" for="filter-client">
                <i data-lucide="building" style="width: 13px; height: 13px; color: var(--ia-info-fg, #60a5fa);"></i>
                <span>Cliente</span>
              </label>
              <select class="filter-select" id="filter-client">
                <option value="">Todos os Clientes</option>
                ${availableClients.map(c => `
                  <option value="${c.id}" ${this.filterClient === c.id ? 'selected' : ''}>${c.name}</option>
                `).join('')}
              </select>
            </div>

            <!-- Filtro Unidade / Área -->
            <div class="filter-group">
              <label class="filter-label" for="filter-unit">
                <i data-lucide="map-pin" style="width: 13px; height: 13px; color: var(--status-success-text);"></i>
                <span>Unidade / Área</span>
              </label>
              <select class="filter-select" id="filter-unit">
                <option value="">Todas as Unidades</option>
                ${availableUnits.map(u => `
                  <option value="${u.id}" ${this.filterUnit === u.id ? 'selected' : ''}>${u.name}</option>
                `).join('')}
              </select>
            </div>

            <!-- Filtro Inspetor -->
            <div class="filter-group">
              <label class="filter-label" for="filter-inspector">
                <i data-lucide="user" style="width: 13px; height: 13px; color: var(--status-warning-text);"></i>
                <span>Inspetor</span>
              </label>
              <select class="filter-select" id="filter-inspector">
                <option value="">Todos os Inspetores</option>
                ${availableInspectors.map(i => `
                  <option value="${i.id}" ${this.filterInspector === i.id ? 'selected' : ''}>${i.name}</option>
                `).join('')}
              </select>
            </div>

            <!-- Filtro Data De / Até -->
            <div class="filter-group filter-group-date">
              <label class="filter-label">
                <i data-lucide="calendar" style="width: 13px; height: 13px; color: var(--ia-info-fg, #a78bfa);"></i>
                <span>Data (Período)</span>
              </label>
              <div class="date-inputs-wrapper">
                <input type="date" class="filter-date-input" id="filter-date-start" value="${this.filterDateStart}" title="Data Inicial" placeholder="De">
                <span style="color: var(--color-text-secondary); font-size: 0.8rem;">até</span>
                <input type="date" class="filter-date-input" id="filter-date-end" value="${this.filterDateEnd}" title="Data Final" placeholder="Até">
              </div>
            </div>
          </div>
        </div>

        <div class="tabs-container">
          <div class="tab ${this.activeMainTab === 'ALL' ? 'active' : ''}" data-tab="ALL">Todos</div>
          <div class="tab ${this.activeMainTab === 'IN_PROGRESS' ? 'active' : ''}" data-tab="IN_PROGRESS">Em andamento</div>
          <div class="tab ${this.activeMainTab === 'COMPLETED' ? 'active' : ''}" data-tab="COMPLETED">Concluídos</div>
        </div>

        ${this.activeMainTab === 'IN_PROGRESS' ? `
          <div class="sub-tabs-container">
            <div class="sub-tab ${this.activeSubTab === 'ALL' ? 'active' : ''}" data-subtab="ALL">Todos</div>
            <div class="sub-tab ${this.activeSubTab === 'IN_ELABORATION' ? 'active' : ''}" data-subtab="IN_ELABORATION">Em elaboração</div>
            <div class="sub-tab ${this.activeSubTab === 'FINALIZED_BY_INSPECTOR' ? 'active' : ''}" data-subtab="FINALIZED_BY_INSPECTOR">Finalizado (Inspetor)</div>
            <div class="sub-tab ${this.activeSubTab === 'IN_ANALYSIS' ? 'active' : ''}" data-subtab="IN_ANALYSIS">Em análise</div>
            <div class="sub-tab ${this.activeSubTab === 'RETURNED' ? 'active' : ''}" data-subtab="RETURNED">Devolvidos/Correção</div>
            <div class="sub-tab ${this.activeSubTab === 'AWAITING_SIGNATURE' ? 'active' : ''}" data-subtab="AWAITING_SIGNATURE">Aguardando assinatura</div>
          </div>
        ` : ''}

        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th>RI</th>
                <th>TAG / Sistema</th>
                <th>Área / Cliente</th>
                <th>Inspetor</th>
                <th>Última Alteração</th>
                <th>Status</th>
                <th style="text-align: right;">Ações</th>
              </tr>
            </thead>
            <tbody>
              ${filteredItems.length === 0 ? `
                <tr>
                  <td colspan="7" style="text-align: center; padding: 40px; color: var(--color-text-secondary);">
                    Nenhum relatório encontrado para os filtros atuais.
                  </td>
                </tr>
              ` : filteredItems.map(({ insp, rep }) => {
                const eq = this.equipments.find(e => e.id === insp.equipmentId);
                const eqTag = eq?.tag || eq?.name || insp.equipmentId || 'TAG N/D';
                const repId = rep?.id || '-';
                const repStatus = rep?.status || 'DRAFT';
                const updatedAt = rep?.updatedAt ? new Date(rep.updatedAt).toLocaleDateString('pt-BR') : new Date(insp.createdAt).toLocaleDateString('pt-BR');
                
                let statusBadge = '';
                let statusText = repStatus;
                
                if (repStatus === 'IN_ELABORATION' || repStatus === 'DRAFT') { statusBadge = 'badge-neutral'; statusText = 'EM ELABORAÇÃO'; }
                else if (repStatus === 'FINALIZED_BY_INSPECTOR') { statusBadge = 'badge-info'; statusText = 'FINALIZADO'; }
                else if (repStatus === 'SUBMITTED_TO_ENGINEERING' || repStatus === 'UNDER_ENGINEERING_REVIEW') { statusBadge = 'badge-warning'; statusText = 'EM ANÁLISE'; }
                else if (repStatus === 'RETURNED' || repStatus === 'IN_CORRECTION') { statusBadge = 'badge-danger'; statusText = 'DEVOLVIDO'; }
                else if (repStatus === 'AWAITING_SIGNATURE') { statusBadge = 'badge-info'; statusText = 'AGUARDANDO ASSINATURA'; }
                else if (repStatus === 'APPROVED' || repStatus === 'SIGNED' || repStatus === 'COMPLETED') { statusBadge = 'badge-success'; statusText = 'CONCLUÍDO'; }

                return `
                  <tr>
                    <td style="font-family: monospace; color: var(--color-graphite-400);">${rep?.reportNumber || insp.reportNumber || (repId !== '-' ? repId.split('-')[0] : insp.id.substring(0,6))}</td>
                    <td style="font-weight: 600; color: var(--color-graphite-200);">${eqTag}</td>
                    <td style="color: var(--color-graphite-300); font-weight: 500;">${this.getClientDisplayName(insp.clientId || rep?.clientId)}</td>
                    <td style="color: var(--color-graphite-300); font-weight: 500;">${this.getInspectorDisplayName(insp, rep)}</td>
                    <td style="color: var(--color-graphite-400);">${updatedAt}</td>
                    <td><span class="badge ${statusBadge}">${statusText}</span></td>
                    <td style="text-align: right; white-space: nowrap;">
                      ${repId !== '-' ? `
                        <button class="btn-action primary btn-view-pdf" data-rep-id="${repId}" data-insp-id="${insp.id}" title="Baixar / Visualizar Relatório">
                          <i data-lucide="download" style="width: 16px; height: 16px;"></i>
                        </button>
                      ` : ''}
                      
                      ${(repStatus === 'IN_ELABORATION' || repStatus === 'DRAFT' || repStatus === 'RETURNED' || repStatus === 'IN_CORRECTION') ? `
                        <button class="btn-action btn-edit" data-insp-id="${insp.id}" title="Editar Checklist" style="border-color: var(--color-ink); color: var(--color-ink);">
                          <i data-lucide="edit" style="width: 16px; height: 16px;"></i>
                        </button>
                        <button class="btn-action success btn-finalize" data-rep-id="${repId}" title="Finalizar Relatório">
                          <i data-lucide="check-square" style="width: 16px; height: 16px;"></i>
                        </button>
                      ` : ''}
                      
                      ${(repStatus === 'FINALIZED_BY_INSPECTOR' || repStatus === 'FINALIZED' || repStatus === 'SUBMITTED_TO_ENGINEERING' || repStatus === 'UNDER_ENGINEERING_REVIEW') ? `
                        <span style="display: inline-flex; align-items: center; gap: 4px; padding: 6px 10px; border-radius: 4px; background: var(--color-graphite-700); color: var(--color-graphite-400); font-size: 0.8rem; font-weight: 500;" title="SOMENTE LEITURA">
                          <i data-lucide="check" style="width: 14px; height: 14px;"></i> FINALIZADO
                        </span>
                      ` : ''}

                      ${(repStatus === 'IN_ELABORATION' || repStatus === 'DRAFT' || repStatus === 'RETURNED' || repStatus === 'IN_CORRECTION') ? `
                      <button class="btn-action danger btn-delete" data-insp-id="${insp.id}" data-rep-id="${repId}" data-eq-tag="${eqTag}" title="Excluir">
                          <i data-lucide="trash-2" style="width: 16px; height: 16px;"></i>
                      </button>
                      ` : ''}
                    </td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>
        
        <div id="delete-modal-container"></div>
        <div id="finalize-modal-container"></div>

        <!-- Document Preview Modal -->
        <div class="doc-modal" id="doc-modal">
          <div class="doc-modal-content">
            <div style="display: flex; justify-content: space-between; align-items: center; background: var(--color-graphite-800); padding: 10px 16px; border-bottom: 1px solid var(--color-graphite-700);">
              <span style="color: var(--color-text-primary); font-weight: 600; font-size: 0.95rem; display: flex; align-items: center; gap: 8px;">
                <i data-lucide="file-text" style="width: 16px; height: 16px; color: var(--ia-info-fg, #60a5fa);"></i>
                Relatório de Integridade Oficial (NR-13) - Pré-visualização
              </span>
              <button type="button" class="doc-modal-close" id="btn-close-modal" style="background: var(--status-danger-solid); border: none; color: var(--color-text-primary); padding: 6px 14px; border-radius: 4px; font-weight: 600; font-size: 0.85rem; cursor: pointer;">
                ✕ Fechar
              </button>
            </div>
            <app-pdf-preview id="pdf-preview" style="flex: 1;"></app-pdf-preview>
          </div>
        </div>
      </div>
    `;

    createIcons({
      icons: { Plus, FileText, Download, Sparkles, Trash2, Eye, CheckCircle, AlertCircle, AlertTriangle, Search, Filter, Edit, CheckSquare, Clock, Calendar, Building, MapPin, User, RotateCcw, X },
      nameAttr: "data-lucide",
      root: this
    });

    this.attachEventListeners();
  }

  private attachEventListeners() {
    const searchInput = this.querySelector('#search-input') as HTMLInputElement;
    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        this.searchTerm = (e.target as HTMLInputElement).value;
        this.render();
      });
    }

    const filterClient = this.querySelector('#filter-client') as HTMLSelectElement;
    if (filterClient) {
      filterClient.addEventListener('change', (e) => {
        const clientVal = (e.target as HTMLSelectElement).value;
        this.filterClient = clientVal;
        contextStore.set({ activeClientId: clientVal || null });
        this.render();
      });
    }

    const filterUnit = this.querySelector('#filter-unit') as HTMLSelectElement;
    if (filterUnit) {
      filterUnit.addEventListener('change', (e) => {
        this.filterUnit = (e.target as HTMLSelectElement).value;
        this.render();
      });
    }

    const filterInspector = this.querySelector('#filter-inspector') as HTMLSelectElement;
    if (filterInspector) {
      filterInspector.addEventListener('change', (e) => {
        this.filterInspector = (e.target as HTMLSelectElement).value;
        this.render();
      });
    }

    const filterDateStart = this.querySelector('#filter-date-start') as HTMLInputElement;
    if (filterDateStart) {
      filterDateStart.addEventListener('change', (e) => {
        this.filterDateStart = (e.target as HTMLInputElement).value;
        this.render();
      });
    }

    const filterDateEnd = this.querySelector('#filter-date-end') as HTMLInputElement;
    if (filterDateEnd) {
      filterDateEnd.addEventListener('change', (e) => {
        this.filterDateEnd = (e.target as HTMLInputElement).value;
        this.render();
      });
    }

    const btnClearFilters = this.querySelector('#btn-clear-filters');
    if (btnClearFilters) {
      btnClearFilters.addEventListener('click', () => {
        this.searchTerm = "";
        this.filterClient = "";
        this.filterUnit = "";
        this.filterInspector = "";
        this.filterDateStart = "";
        this.filterDateEnd = "";
        this.render();
      });
    }

    this.querySelectorAll('.tab').forEach(tab => {
      tab.addEventListener('click', (e) => {
        this.activeMainTab = (e.currentTarget as HTMLElement).getAttribute('data-tab') as any;
        this.activeSubTab = 'ALL';
        this.render();
      });
    });

    this.querySelectorAll('.sub-tab').forEach(tab => {
      tab.addEventListener('click', (e) => {
        this.activeSubTab = (e.currentTarget as HTMLElement).getAttribute('data-subtab') as string;
        this.render();
      });
    });

    this.querySelector('#btn-new-inspection')?.addEventListener('click', () => {
      router.navigate('/inspections/new');
    });

    this.querySelectorAll('.btn-edit').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const inspId = (e.currentTarget as HTMLElement).getAttribute('data-insp-id');
        if (inspId) router.navigate('/inspections/checklist/' + inspId);
      });
    });

    this.querySelectorAll('.btn-finalize').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const repId = (e.currentTarget as HTMLElement).getAttribute('data-rep-id');
        if (!repId) return;
        
        try {
          const p = window.app.platform;
          const activeClientId = contextStore.get().activeClientId;
          const ctx = { activeClientId, permissions: contextStore.get().permissions, userId: contextStore.get().userId } as any;
          
          const validation = await p.reportService.validateIntegrityReport(ctx, repId as any);
          if (validation.hasImpeditive) {
            alert("Não é possível finalizar. Existem pendências impeditivas:\n\n" + validation.errors.filter(err => err.type === 'IMPEDITIVE').map(err => "- " + err.message).join('\n'));
            return;
          }

          const modalContainer = this.querySelector('#finalize-modal-container');
          if (!modalContainer) return;

          modalContainer.innerHTML = `
            <div class="delete-modal-backdrop" style="position: fixed; inset: 0; background: rgba(0, 0, 0, 0.75); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(4px);">
              <div class="delete-modal-box" style="background: var(--color-graphite-800); border: 1px solid var(--color-graphite-700); border-radius: 12px; padding: 24px; max-width: 460px; width: 92%; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5); color: var(--color-text-primary);">
                <div style="font-size: 1.15rem; font-weight: 700; color: var(--status-success-text); display: flex; align-items: center; gap: 8px; margin-bottom: 12px;">
                  <i data-lucide="shield-check" style="width: 22px; height: 22px;"></i>
                  <span>Finalizar Relatório de Integridade?</span>
                </div>
                <div style="font-size: 0.9rem; color: var(--color-graphite-300); line-height: 1.5; margin-bottom: 20px;">
                  Ao finalizar este relatório, o conteúdo será bloqueado para edição pelo Inspetor e ficará disponível para análise do Engenheiro/PLH responsável pelo Projeto.
                </div>
                <div style="display: flex; gap: 12px; justify-content: flex-end;">
                  <button type="button" id="btn-cancel-finalize" style="background: transparent; border: 1px solid var(--color-graphite-700); color: var(--color-graphite-300); padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer;">
                    Cancelar
                  </button>
                  <button type="button" id="btn-confirm-finalize" style="background: var(--status-success-text); border: none; color: var(--color-text-primary); padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer;">
                    Confirmar Finalização
                  </button>
                </div>
              </div>
            </div>
          `;

          if ((window as any).lucide) {
             (window as any).lucide.createIcons();
          }

          const btnCancel = modalContainer.querySelector('#btn-cancel-finalize');
          const btnConfirm = modalContainer.querySelector('#btn-confirm-finalize');

          btnCancel?.addEventListener('click', () => {
            modalContainer.innerHTML = '';
          });

          btnConfirm?.addEventListener('click', async () => {
            btnConfirm.innerHTML = 'Finalizando...';
            (btnConfirm as HTMLButtonElement).disabled = true;
            (btnCancel as HTMLButtonElement).disabled = true;
            try {
              const report = await p.reportService.getReportById(ctx, repId as any);
              if (report) {
                report.status = 'FINALIZED_BY_INSPECTOR';
                report.updatedAt = new Date().toISOString();
                await p.reportService.updateReport(ctx, report);
                await this.loadData();
              }
            } catch (err: any) {
              alert("Erro ao finalizar: " + err.message);
            } finally {
              modalContainer.innerHTML = '';
            }
          });

        } catch (err: any) {
          alert("Erro: " + err.message);
        }
      });
    });

    this.querySelectorAll('.btn-view-pdf').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const repId = (e.currentTarget as HTMLElement).getAttribute('data-rep-id');
        if (repId) router.navigate('/reports/integrity/view/' + repId);
      });
    });

    this.querySelectorAll('.btn-delete').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const currentTarget = e.currentTarget as HTMLElement;
        const inspId = currentTarget.getAttribute('data-insp-id') || '';
        const repId = currentTarget.getAttribute('data-rep-id') || '';
        const eqTag = currentTarget.getAttribute('data-eq-tag') || 'Equipamento';
        this.showDeleteModal(inspId, repId, eqTag);
      });
    });

    const modal = this.querySelector('#doc-modal') as HTMLElement;
    const btnClose = this.querySelector('#btn-close-modal');

    const closeModal = () => modal?.classList.remove('active');
    btnClose?.addEventListener('click', closeModal);
  }

  private showDeleteModal(inspId: string, repId: string, eqTag: string) {
    const modalContainer = this.querySelector('#delete-modal-container');
    if (!modalContainer) return;

    modalContainer.innerHTML = `
      <div class="delete-modal-backdrop" style="position: fixed; inset: 0; background: rgba(0, 0, 0, 0.75); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(4px);">
        <div class="delete-modal-box" style="background: var(--color-graphite-800); border: 1px solid var(--color-graphite-700); border-radius: 12px; padding: 24px; max-width: 460px; width: 92%; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5); color: var(--color-text-primary);">
          <div style="font-size: 1.15rem; font-weight: 700; color: var(--status-danger-solid); display: flex; align-items: center; gap: 8px; margin-bottom: 12px;">
            <i data-lucide="alert-triangle" style="width: 22px; height: 22px;"></i>
            <span>Confirmar Exclusão</span>
          </div>
          <div style="font-size: 0.9rem; color: var(--color-graphite-300); line-height: 1.5; margin-bottom: 20px;">
            Tem certeza de que deseja excluir permanentemente o registro de inspeção do equipamento <strong style="color: var(--color-text-primary);">${eqTag}</strong>?
            <br><br>
            <span style="color: var(--status-danger-border); font-size: 0.825rem; display: block; background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.2); padding: 8px 12px; border-radius: 6px;">
              ⚠️ Esta ação removerá definitivamente o checklist, fotos, ensaios não destrutivos e o Laudo de Integridade (RI) vinculado.
            </span>
          </div>
          <div style="display: flex; justify-content: flex-end; gap: 10px;">
            <button type="button" id="btn-cancel-delete" style="background: var(--color-primary); border: 1px solid var(--color-graphite-700); color: var(--color-graphite-300); padding: 8px 16px; border-radius: 6px; cursor: pointer; font-size: 0.875rem; font-weight: 500;">
              Cancelar
            </button>
            <button type="button" id="btn-confirm-delete" style="background: var(--status-danger-solid); border: 1px solid var(--status-danger-solid); color: var(--color-text-primary); padding: 8px 16px; border-radius: 6px; cursor: pointer; font-size: 0.875rem; font-weight: 600; display: flex; align-items: center; gap: 6px;">
              <i data-lucide="trash-2" style="width: 15px; height: 15px;"></i>
              <span id="btn-confirm-delete-text">Excluir Registro</span>
            </button>
          </div>
        </div>
      </div>
    `;

    createIcons({
      icons: { AlertTriangle, Trash2 },
      nameAttr: "data-lucide",
      root: modalContainer
    });

    const closeModal = () => {
      modalContainer.innerHTML = '';
    };

    modalContainer.querySelector('#btn-cancel-delete')?.addEventListener('click', closeModal);
    modalContainer.querySelector('.delete-modal-backdrop')?.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('delete-modal-backdrop')) {
        closeModal();
      }
    });

    modalContainer.querySelector('#btn-confirm-delete')?.addEventListener('click', async () => {
      const confirmBtn = modalContainer.querySelector('#btn-confirm-delete') as HTMLButtonElement;
      const confirmText = modalContainer.querySelector('#btn-confirm-delete-text') as HTMLElement;
      if (confirmBtn) confirmBtn.disabled = true;
      if (confirmText) confirmText.textContent = 'Excluindo...';

      try {
        const p = window.app.platform;
        const activeClientId = contextStore.get().activeClientId;
        const ctx = { 
          activeClientId, 
          userId: contextStore.get().userId,
          permissions: contextStore.get().permissions 
        } as any;

        // 1. Delete integrity report if exists
        if (repId && repId !== '-') {
          try {
            await p.reportService.deleteReport(ctx, repId as IntegrityReportId);
          } catch (err) {
            console.warn("Aviso ao excluir relatório:", err);
          }
        } else {
          const rep = this.reports.find(r => r.inspectionId === inspId);
          if (rep?.id) {
            try {
              await p.reportService.deleteReport(ctx, rep.id);
            } catch (err) {
              console.warn("Aviso ao excluir relatório vinculado:", err);
            }
          }
        }

        // 2. Delete inspection
        if (inspId) {
          try {
            await p.inspectionService.deleteInspection(ctx, inspId as InspectionId);
          } catch (err) {
            console.warn("Aviso ao excluir inspeção:", err);
          }
        }

        // 3. Clear any local storage items
        try {
          if (typeof localStorage !== 'undefined') {
            localStorage.removeItem(`ri_checklist_insp_${inspId}`);
            localStorage.removeItem(`ri_performed_tests_${inspId}`);
            localStorage.removeItem(`ri_ref_docs_${inspId}`);
            if (repId && repId !== '-') {
              localStorage.removeItem(`ri_checklist_${repId}`);
              localStorage.removeItem(`ri_cache_${repId}`);
            }
          }
        } catch {}

        closeModal();
        await this.loadData();
      } catch (err: any) {
        alert("Erro ao excluir: " + (err.message || 'Falha na operação'));
        if (confirmBtn) confirmBtn.disabled = false;
        if (confirmText) confirmText.textContent = 'Excluir Registro';
      }
    });
  }

  private async showPdfPreview(repId: string, inspId: string | null) {
    const modal = this.querySelector('#doc-modal') as HTMLElement;
    const pdfPreview = this.querySelector('#pdf-preview') as any;
    try {
      const p = window.app.platform;
      const ctx = { activeClientId: contextStore.get().activeClientId, permissions: contextStore.get().permissions, userId: contextStore.get().userId } as any;
      const report = await p.reportService.getReportById(ctx, repId as IntegrityReportId);
      if (!report) throw new Error("Laudo não encontrado");
      
      const effectiveInspId = inspId || report.inspectionId;
      let eq: any = null;
      let inspection: any = null;
      if (effectiveInspId) {
        inspection = await p.inspectionService.getInspectionById(ctx, effectiveInspId as InspectionId);
        if (inspection?.equipmentId) {
          eq = await p.equipmentService.getEquipment(ctx, inspection.equipmentId);
        }
      }
      
      pdfPreview.snapshot = {
        id: report.id,
        type: 'RI',
        revision: '00',
        createdAt: new Date(report.createdAt || Date.now()),
        clientId: report.clientId ?? eq?.clientId,
        data: {
          clientName: 'Cliente',
          report: {
            id: report.id,
            status: report.status || 'DRAFT',
            inspectorName: report.inspectorId || 'Inspetor',
          },
          equipment: {
            tag: eq?.tag || eq?.name || '',
          },
          checklistComponents: [],
          recommendations: [],
          signatories: []
        }
      };
      modal.classList.add('active');
    } catch (err: any) {
      alert("Erro ao carregar pré-visualização: " + err.message);
    }
  }
}

if (!customElements.get("app-inspections-page")) {
  customElements.define("app-inspections-page", AppInspectionsPage);
}



